import qs from "qs";
import { createSlice, current } from "@reduxjs/toolkit";
import { formatAxiosErrorMessage, qargsToQstring } from "../../../utils/common";
import apiService from "../../../api/apiService";
import { endPoints } from "../../../api/endpoints";
import {
  removeArrayItem,
  removeArrayObjectItem,
  removeDuplicates,
  replaceArrayObjectItem,
} from "../../../utils/util";

const unwrap = (payload) => payload?.data ?? payload ?? null;

const BOARD_LANES = ["overdue", "today", "tomorrow", "later"];
const BOARD_LABELS = {
  overdue: "Overdue",
  today: "Today",
  tomorrow: "Tomorrow",
  later: "Later",
};

/**
 * Prepend a note (due=null) into the global notes feed without refetching.
 * Initializes results shape if the list has not been loaded yet.
 */
const prependNoteToNotesList = (state, created) => {
  if (!created?.id || created.due != null) return;

  if (
    !state.notesList.data ||
    Array.isArray(state.notesList.data) ||
    !Array.isArray(state.notesList.data.results)
  ) {
    const prevCount = Array.isArray(state.notesList.data)
      ? 0
      : state.notesList.data?.count || 0;
    state.notesList.data = {
      count: prevCount,
      next: null,
      previous: null,
      results: [],
    };
  }

  const results = state.notesList.data.results;
  const existingIdx = results.findIndex(
    (n) => String(n.id) === String(created.id)
  );
  if (existingIdx >= 0) {
    // Move updated copy to top
    results.splice(existingIdx, 1);
    results.unshift(created);
  } else {
    results.unshift(created);
    state.notesList.data.count = (state.notesList.data.count || 0) + 1;
  }
};
const BOARD_PAGE_SIZE = 10;

const emptyBoardLane = (label = "") => ({
  total: 0,
  has_more: false,
  tasks: [],
  label,
});

const emptyBoardLanes = () => ({
  overdue: emptyBoardLane(BOARD_LABELS.overdue),
  today: emptyBoardLane(BOARD_LABELS.today),
  tomorrow: emptyBoardLane(BOARD_LABELS.tomorrow),
  later: emptyBoardLane(BOARD_LABELS.later),
});

const normalizeLane = (lane, fallbackLabel = "") => ({
  total: lane?.total ?? 0,
  has_more: Boolean(lane?.has_more),
  tasks: Array.isArray(lane?.tasks) ? lane.tasks : [],
  label: lane?.label || fallbackLabel,
});

const TaskInitialState = {
  list: {
    isLoading: false,
    isLoadingMore: false,
    data: [],
    error: null,
  },
  // Isolated notes list so global Notes page does not clobber Task list/board
  notesList: {
    isLoading: false,
    isLoadingMore: false,
    data: [],
    error: null,
  },
  notesExport: {
    isLoading: false,
    isSuccess: false,
    error: null,
  },
  tasksExport: {
    isLoading: false,
    isSuccess: false,
    error: null,
  },
  // Sales kanban from GET /task/board/
  board: {
    isLoading: false,
    loadingLane: null,
    error: null,
    lanes: emptyBoardLanes(),
  },
  create: {
    isLoading: false,
    isSuccess: false,
    data: null,
    error: null,
  },
  put: {
    isLoading: false,
    isSuccess: false,
    data: null,
    error: null,
  },
  delete: {
    isLoading: false,
    isSuccess: false,
    error: null,
  },
  inProcess: {
    queue: [],
  },
};

export const TaskSlice = createSlice({
  name: "task",
  initialState: {
    ...TaskInitialState,
  },
  reducers: {
    taskListRequesting: (state, action) => {
      state.list.isLoading = true;
      state.list.isLoadingMore = false;
    },
    taskListLoadMoreRequesting: (state) => {
      state.list.isLoadingMore = true;
    },
    taskListSuccess: (state, action) => {
      const { list } = current(state);
      const newData = action.payload.data;
      if (list?.data?.results) {
        newData.results = removeDuplicates([
          ...list.data.results,
          ...newData.results,
        ]);
      }
      state.list.isLoading = false;
      state.list.isLoadingMore = false;
      state.list.error = null;
      state.list.data = newData;
    },
    taskListRefreshSuccess: (state, action) => {
      state.list.isLoading = false;
      state.list.isLoadingMore = false;
      state.list.error = null;
      state.list.data = action.payload.data;
    },
    taskListError: (state, action) => {
      state.list.isLoading = false;
      state.list.isLoadingMore = false;
      state.list.error = action.payload;
    },
    taskListReset: (state, action) => {
      state.list = TaskInitialState.list;
    },

    // Global Notes page list (GET /task/?due__isnull=true) — separate from task.list
    notesListRequesting: (state) => {
      state.notesList.isLoading = true;
      state.notesList.isLoadingMore = false;
    },
    notesListLoadMoreRequesting: (state) => {
      state.notesList.isLoadingMore = true;
    },
    // Append page for infinite scroll
    notesListSuccess: (state, action) => {
      const {notesList} = current(state);
      const newData = action.payload.data;
      if (notesList?.data?.results) {
        newData.results = removeDuplicates([
          ...notesList.data.results,
          ...newData.results,
        ]);
      }
      state.notesList.isLoading = false;
      state.notesList.isLoadingMore = false;
      state.notesList.error = null;
      state.notesList.data = newData;
    },
    // Replace list on filter / first page refresh
    notesListRefreshSuccess: (state, action) => {
      state.notesList.isLoading = false;
      state.notesList.isLoadingMore = false;
      state.notesList.error = null;
      state.notesList.data = action.payload.data;
    },
    notesListError: (state, action) => {
      state.notesList.isLoading = false;
      state.notesList.isLoadingMore = false;
      state.notesList.error = action.payload;
    },
    notesListReset: (state) => {
      state.notesList = TaskInitialState.notesList;
    },
    // Optimistic insert for note creates (modal + WhatsApp panel)
    notesListPrependNote: (state, action) => {
      prependNoteToNotesList(state, unwrap(action.payload) || action.payload);
    },

    notesExportRequesting: (state) => {
      state.notesExport.isLoading = true;
      state.notesExport.isSuccess = false;
      state.notesExport.error = null;
    },
    notesExportSuccess: (state) => {
      state.notesExport.isLoading = false;
      state.notesExport.isSuccess = true;
      state.notesExport.error = null;
    },
    notesExportError: (state, action) => {
      state.notesExport.isLoading = false;
      state.notesExport.isSuccess = false;
      state.notesExport.error = action.payload;
    },
    notesExportReset: (state) => {
      state.notesExport = TaskInitialState.notesExport;
    },

    tasksExportRequesting: (state) => {
      state.tasksExport.isLoading = true;
      state.tasksExport.isSuccess = false;
      state.tasksExport.error = null;
    },
    tasksExportSuccess: (state) => {
      state.tasksExport.isLoading = false;
      state.tasksExport.isSuccess = true;
      state.tasksExport.error = null;
    },
    tasksExportError: (state, action) => {
      state.tasksExport.isLoading = false;
      state.tasksExport.isSuccess = false;
      state.tasksExport.error = action.payload;
    },
    tasksExportReset: (state) => {
      state.tasksExport = TaskInitialState.tasksExport;
    },

    taskBoardRequesting: (state) => {
      state.board.isLoading = true;
      state.board.loadingLane = null;
      state.board.error = null;
    },
    taskBoardLaneRequesting: (state, action) => {
      state.board.loadingLane = action.payload;
      state.board.error = null;
    },
    taskBoardSuccess: (state, action) => {
      const payload = unwrap(action.payload) || {};
      const next = emptyBoardLanes();
      BOARD_LANES.forEach((key) => {
        if (payload[key]) {
          next[key] = normalizeLane(payload[key], next[key].label);
        }
      });
      state.board.lanes = next;
      state.board.isLoading = false;
      state.board.loadingLane = null;
      state.board.error = null;
    },
    taskBoardLaneAppendSuccess: (state, action) => {
      const { stage, lane } = action.payload;
      const prev = state.board.lanes[stage] || emptyBoardLanes()[stage];
      const incoming = normalizeLane(lane, prev.label);
      state.board.lanes[stage] = {
        ...incoming,
        tasks: removeDuplicates([...(prev.tasks || []), ...(incoming.tasks || [])]),
      };
      state.board.loadingLane = null;
      state.board.error = null;
    },
    taskBoardError: (state, action) => {
      state.board.isLoading = false;
      state.board.loadingLane = null;
      state.board.error = action.payload;
    },
    taskBoardReset: (state) => {
      state.board = TaskInitialState.board;
    },

    taskCreateRequesting: (state, action) => {
      state.create.isLoading = true;
      state.create.isSuccess = false;
    },
    taskCreateSuccess: (state, action) => {
      state.create.isSuccess = true;
      state.create.isLoading = false;
      state.create.error = null;
      const created = unwrap(action.payload);
      state.create.data = created;
      if (created && state.list.data?.results) {
        state.list.data.results.push(created);
      }
      // Keep global notes feed in sync when a note (due=null) is created
      prependNoteToNotesList(state, created);
    },
    taskCreateError: (state, action) => {
      state.create.isLoading = false;
      state.create.isSuccess = false;
      state.create.error = action.payload;
    },
    taskCreateReset: (state, action) => {
      state.create = TaskInitialState.create;
    },

    taskUpdateRequesting: (state, action) => {
      state.put.isLoading = true;
      state.put.isSuccess = false;
    },
    taskUpdateSuccess: (state, action) => {
      const { list } = current(state);
      const updated = unwrap(action.payload);
      state.put.isSuccess = true;
      state.put.isLoading = false;
      state.put.error = null;
      state.put.data = updated;

      if (state.list.data?.results && updated) {
        let temp = [...(list?.data?.results ?? [])];
        state.list.data.results = replaceArrayObjectItem(temp, updated);
      }
      // Sync notes feed after note edits
      if (updated?.id && updated.due == null && state.notesList.data?.results) {
        state.notesList.data.results = replaceArrayObjectItem(
          [...state.notesList.data.results],
          updated
        );
      }

      // Keep board columns in sync after PATCH complete / edits
      if (updated?.id && state.board?.lanes) {
        BOARD_LANES.forEach((key) => {
          const lane = state.board.lanes[key];
          if (!lane?.tasks?.length) return;
          if (updated.status === "completed") {
            const before = lane.tasks.length;
            lane.tasks = lane.tasks.filter(
              (t) => String(t.id) !== String(updated.id)
            );
            if (lane.tasks.length !== before) {
              lane.total = Math.max(0, (lane.total || 0) - 1);
            }
          } else {
            lane.tasks = replaceArrayObjectItem([...lane.tasks], updated);
          }
        });
      }
    },
    taskBulkUpdateSuccess: (state, action) => {
      const { list } = current(state);
      state.put.isSuccess = true;
      state.put.isLoading = false;
      state.put.error = null;
      let temp = [...(list?.data?.results ?? [])];
      action.payload.data.forEach((item) => {
        temp = replaceArrayObjectItem(temp, item);
      });
      state.list.data.results = temp;
    },
    taskUpdateError: (state, action) => {
      state.put.isLoading = false;
      state.put.isSuccess = false;
      state.put.error = action.payload;
    },
    taskUpdateReset: (state, action) => {
      state.put = TaskInitialState.put;
    },

    taskDeleteRequesting: (state, action) => {
      state.delete.isLoading = true;
      state.delete.isSuccess = false;
    },
    taskDeleteSuccess: (state, action) => {
      state.delete.isSuccess = true;
      state.delete.isLoading = false;
      state.delete.error = null;
      const deletedId = action.payload;
      if (state.list.data.results) {
        state.list.data.results = removeArrayObjectItem(
          state.list.data.results,
          deletedId
        );
      }
      if (state.notesList.data?.results && deletedId) {
        const before = state.notesList.data.results.length;
        state.notesList.data.results = removeArrayObjectItem(
          state.notesList.data.results,
          deletedId
        );
        if (state.notesList.data.results.length !== before) {
          state.notesList.data.count = Math.max(
            0,
            (state.notesList.data.count || 0) - 1
          );
        }
      }
      if (state.board?.lanes && deletedId) {
        BOARD_LANES.forEach((key) => {
          const lane = state.board.lanes[key];
          if (!lane?.tasks?.length) return;
          const before = lane.tasks.length;
          lane.tasks = lane.tasks.filter(
            (t) => String(t.id) !== String(deletedId)
          );
          if (lane.tasks.length !== before) {
            lane.total = Math.max(0, (lane.total || 0) - 1);
          }
        });
      }
    },
    taskBulkDeleteSuccess: (state, action) => {
      state.delete.isSuccess = true;
      state.delete.isLoading = false;
      state.delete.error = null;
      action.payload.data.forEach((item) => {
        if (state.list.data?.results) {
          state.list.data.results = removeArrayObjectItem(
            state.list.data.results,
            item
          );
        }
        if (state.notesList.data?.results) {
          state.notesList.data.results = removeArrayObjectItem(
            state.notesList.data.results,
            item
          );
        }
      });
    },
    taskDeleteError: (state, action) => {
      state.delete.isLoading = false;
      state.delete.isSuccess = false;
      state.delete.error = action.payload;
    },
    taskDeleteReset: (state, action) => {
      state.delete = TaskInitialState.delete;
    },

    taskQueueAdd: (state, action) => {
      if (!state.inProcess.queue.includes(action.payload)) {
        state.inProcess.queue.push(action.payload);
      }
    },
    taskQueueRemove: (state, action) => {
      state.inProcess.queue = removeArrayItem(
        state.inProcess.queue,
        action.payload
      );
    },
    taskQueueReset: (state, action) => {
      state.inProcess = TaskInitialState.inProcess;
    },

    taskMasterReset: () => TaskInitialState,
  },
});

export const getTaskListAPI = (query) => async (dispatch) => {
  // Replace on first page / when offset omitted; append when offset > 0
  const isRefresh =
    query.offset === undefined ||
    query.offset === null ||
    query.offset === 0 ||
    query.offset === "0";
  if (isRefresh) {
    dispatch(taskListRequesting());
  } else {
    dispatch(taskListLoadMoreRequesting());
  }
  let stringQuery = qargsToQstring(query);
  try {
    const response = await apiService.get(
      `${endPoints.task.list}${stringQuery}`,
      {}
    );
    if (isRefresh) {
      dispatch(taskListRefreshSuccess(response.data));
    } else {
      dispatch(taskListSuccess(response.data));
    }
    return response?.data;
  } catch (error) {
    dispatch(taskListError(formatAxiosErrorMessage(error)));
    return null;
  }
};

/**
 * GET /task/?due__isnull=true&client=&created_on_after=&created_on_before=&q=&ordering=-created_on&limit=&offset=
 * Isolated from task.list so Task board/list state is preserved.
 * offset 0 / omitted → replace; offset > 0 → append (infinite scroll).
 */
export const getNotesListAPI = (query = {}) => async (dispatch) => {
  const isRefresh =
    query.offset === undefined ||
    query.offset === null ||
    query.offset === 0 ||
    query.offset === "0";
  if (isRefresh) {
    dispatch(notesListRequesting());
  } else {
    dispatch(notesListLoadMoreRequesting());
  }
  // Always force notes-only (due=null); callers cannot omit due__isnull
  const stringQuery = qargsToQstring({
    ...query,
    due__isnull: true,
  });
  try {
    const response = await apiService.get(
      `${endPoints.task.list}${stringQuery}`,
      {}
    );
    if (isRefresh) {
      dispatch(notesListRefreshSuccess(response.data));
    } else {
      dispatch(notesListSuccess(response.data));
    }
    return response?.data;
  } catch (error) {
    dispatch(notesListError(formatAxiosErrorMessage(error)));
    return null;
  }
};

/**
 * GET /task/export/?due__isnull=true&client=&created_on_after=&created_on_before=&q=&ordering=-created_on
 * Downloads CSV of notes matching current filters (no pagination).
 */
export const exportNotesListAPI = (query = {}) => async (dispatch) => {
  dispatch(notesExportRequesting());
  // Mirror list: always force notes-only even if backend also filters
  const stringQuery = qargsToQstring({
    ...query,
    due__isnull: true,
    ordering: query.ordering || "-created_on",
  });
  try {
    const response = await apiService.get(
      `${endPoints.task.export}${stringQuery}`,
      {
        responseType: "blob",
        "Access-Control-Expose-Headers": "*",
      }
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "export_notes.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    dispatch(notesExportSuccess());
    return true;
  } catch (error) {
    dispatch(notesExportError(formatAxiosErrorMessage(error)));
    return false;
  }
};

/**
 * GET /task/export-tasks/?due_after=&due_before=&client=&q=&status=&ordering=
 * Downloads CSV of tasks (due not null) matching current filters.
 */
export const exportTasksListAPI = (query = {}) => async (dispatch) => {
  dispatch(TaskSlice.actions.tasksExportRequesting());
  const stringQuery = qargsToQstring({
    ...query,
    due__isnull: false,
    ordering: query.ordering || "due",
  });
  try {
    const response = await apiService.get(
      `${endPoints.task.exportTasks}${stringQuery}`,
      {
        responseType: "blob",
        "Access-Control-Expose-Headers": "*",
      }
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "export_tasks.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    dispatch(TaskSlice.actions.tasksExportSuccess());
    return true;
  } catch (error) {
    dispatch(TaskSlice.actions.tasksExportError(formatAxiosErrorMessage(error)));
    return false;
  }
};

/**
 * GET /task/board/?status=&stage=&limit=&offset=&q=
 * Without stage → refresh all lanes. With stage → append that lane.
 */
export const getTaskBoardAPI = (query = {}) => async (dispatch) => {
  const {
    stage = undefined,
    status = undefined,
    limit = BOARD_PAGE_SIZE,
    offset = 0,
    q = undefined,
    ...rest
  } = query;

  const isLaneAppend = Boolean(stage) && Number(offset) > 0;
  if (isLaneAppend) {
    dispatch(taskBoardLaneRequesting(stage));
  } else {
    dispatch(taskBoardRequesting());
  }

  const params = {
    ...(status ? {status} : {}),
    ...(stage ? {stage} : {}),
    ...(q ? {q} : {}),
    limit,
    offset,
    ...rest,
  };

  try {
    const response = await apiService.get(
      `${endPoints.task.board}${qargsToQstring(params)}`,
      {}
    );
    const body = unwrap(response.data) || {};
    if (isLaneAppend && stage) {
      dispatch(
        taskBoardLaneAppendSuccess({
          stage,
          lane: body[stage] || body,
        })
      );
    } else {
      dispatch(taskBoardSuccess(response.data));
    }
    return true;
  } catch (error) {
    dispatch(taskBoardError(formatAxiosErrorMessage(error)));
    return false;
  }
};

/**
 * Create a task. Pass { silent: true } to skip create.isSuccess side-effects
 * (toasts / list refresh) — used by the feedback-complete flow.
 */
export const createNewTaskAPI = (data, { silent = false } = {}) => async (dispatch) => {
  dispatch(taskCreateRequesting());
  try {
    const response = await apiService.post(endPoints.task.list, data);
    if (silent) {
      // Clear loading without flipping isSuccess (avoids page-level toast/refresh)
      dispatch(taskCreateReset());
    } else {
      dispatch(taskCreateSuccess(response.data));
    }
    // Return created task so callers can refresh UI optimistically
    return response?.data?.data ?? response?.data ?? true;
  } catch (error) {
    dispatch(taskCreateError(formatAxiosErrorMessage(error)));
    return false;
  }
};

export const updateTaskNewTaskAPI = (id, data) => async (dispatch) => {
  dispatch(taskUpdateRequesting());
  dispatch(taskQueueAdd(id));
  try {
    const response = await apiService.patch(
      `${endPoints.task.list}${id}/`,
      data
    );
    dispatch(taskUpdateSuccess(response.data));
    dispatch(taskQueueRemove(id));
    return true;
  } catch (error) {
    dispatch(taskUpdateError(formatAxiosErrorMessage(error)));
    dispatch(taskQueueRemove(id));
    return false;
  }
};

export const deleteTaskNewTaskAPI = (id, params = {}) => async (dispatch) => {
  dispatch(taskDeleteRequesting());
  const queryString = Object.keys(params).length
    ? "?" + qs.stringify(params, { arrayFormat: "repeat" })
    : "";

  apiService
    .delete(`${endPoints.task.list}${id}/${queryString}`, {})
    .then((response) => {
      dispatch(taskDeleteSuccess(id));
    })
    .catch((error) => {
      dispatch(taskDeleteError(formatAxiosErrorMessage(error)));
    });
};

export const deleteBulkTaskAPI = (data) => async (dispatch) => {
  dispatch(taskDeleteRequesting());
  apiService
    .delete(`${endPoints.task.bulkDelete}`, { data })
    .then((response) => {
      dispatch(taskBulkDeleteSuccess(response.data));
    })
    .catch((error) => {
      dispatch(taskDeleteError(formatAxiosErrorMessage(error)));
    });
};

export const updateBulkTaskAPI = (data) => async (dispatch) => {
  dispatch(taskUpdateRequesting());

  data.tasks.forEach((item) => {
    dispatch(taskQueueAdd(item));
  });
  apiService
    .put(`${endPoints.task.bulkCompletion}`, data)
    .then((response) => {
      dispatch(taskBulkUpdateSuccess(response.data));
      response.data.data.forEach((item) => {
        dispatch(taskQueueRemove(item.id));
      });
    })
    .catch((error) => {
      dispatch(taskUpdateError(formatAxiosErrorMessage(error)));
    });
};

export const {
  taskListRequesting,
  taskListLoadMoreRequesting,
  taskListSuccess,
  taskListRefreshSuccess,
  taskListError,
  taskListReset,

  notesListRequesting,
  notesListLoadMoreRequesting,
  notesListSuccess,
  notesListRefreshSuccess,
  notesListError,
  notesListReset,
  notesListPrependNote,

  notesExportRequesting,
  notesExportSuccess,
  notesExportError,
  notesExportReset,

  tasksExportRequesting,
  tasksExportSuccess,
  tasksExportError,
  tasksExportReset,

  taskBoardRequesting,
  taskBoardLaneRequesting,
  taskBoardSuccess,
  taskBoardLaneAppendSuccess,
  taskBoardError,
  taskBoardReset,

  taskCreateRequesting,
  taskCreateSuccess,
  taskCreateError,
  taskCreateReset,

  taskUpdateRequesting,
  taskUpdateSuccess,
  taskUpdateError,
  taskUpdateReset,

  taskDeleteRequesting,
  taskDeleteSuccess,
  taskDeleteError,
  taskDeleteReset,

  taskQueueAdd,
  taskQueueRemove,
  taskQueueReset,

  taskBulkDeleteSuccess,

  taskBulkUpdateSuccess,

  taskMasterReset,
} = TaskSlice.actions;
export const TaskState = (state) => state.task;
export default TaskSlice.reducer;
